// Auth is cookie-based (Azure SSO → httpOnly session cookies). The SPA calls the
// backend cross-origin at VITE_BACKEND_URL with credentials, and echoes the
// readable csrf_token cookie in the X-CSRF-Token header on mutating requests.
const BACKEND = (import.meta.env.VITE_BACKEND_URL || "http://localhost:8080").replace(
  /\/$/,
  "",
);

export function azureLoginUrl(): string {
  return `${BACKEND}/auth/azure/login`;
}

// `reason` is the machine-readable `error` key of a structured
// HTTPException detail (e.g. `{"error": "scenarios_disabled", "message": …}`),
// which the backend envelope carries in `error.details`. Absent for plain
// string details, validation errors and network failures.
export type ApiError = { status: number; message: string; reason?: string };

export function isApiError(error: unknown): error is ApiError {
  return (
    typeof error === "object" &&
    error !== null &&
    "status" in error &&
    typeof error.status === "number" &&
    "message" in error &&
    typeof error.message === "string"
  );
}

let refreshPromise: Promise<boolean> | null = null;

function _csrfToken(): string | null {
  if (typeof document === "undefined") return null;
  const m = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/);
  return m && m[1] ? decodeURIComponent(m[1]) : null;
}

function _handle401() {
  // Tell the AuthProvider (this tab) the session is gone so it bounces to the
  // sign-in screen.
  if (typeof window !== "undefined") {
    try {
      window.dispatchEvent(new Event("sci:auth-cleared"));
    } catch {
      // ignore
    }
  }
}

function _authRefreshable(path: string): boolean {
  return path !== "/auth/refresh" && path !== "/auth/logout";
}

async function _fetch(path: string, init: RequestInit): Promise<Response> {
  const url = `${BACKEND}${path}`;
  const headers = new Headers(init.headers || {});
  if (init.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const method = (init.method || "GET").toUpperCase();
  if (method !== "GET" && method !== "HEAD") {
    const csrf = _csrfToken();
    if (csrf) headers.set("X-CSRF-Token", csrf);
  }
  try {
    return await fetch(url, { ...init, headers, credentials: "include" });
  } catch {
    // fetch() throws a bare TypeError ("Failed to fetch") on network-level
    // failures (server unreachable, connection reset, CORS block) — there is no
    // HTTP status. Tag it as status 0 so callers can tell "can't reach the
    // server" (transient/retryable) apart from a real HTTP error like 401/403.
    // Message stays neutral (no "Retrying…"): it is shared by queries AND
    // mutations, and mutations never auto-retry (only queries do, once — see
    // lib/queryClient `shouldRetry`). The genuine "retrying" signal lives in the
    // self-contained /auth/me reconnect banner in App.tsx, not here.
    const err: ApiError = {
      status: 0,
      message: "Couldn't reach the server.",
    };
    throw err;
  }
}

async function _refreshSession(): Promise<boolean> {
  refreshPromise ??= (async () => {
    const headers = new Headers();
    const csrf = _csrfToken();
    if (csrf) headers.set("X-CSRF-Token", csrf);
    try {
      const res = await fetch(`${BACKEND}/auth/refresh`, {
        method: "POST",
        credentials: "include",
        headers,
      });
      return res.ok;
    } catch {
      return false;
    }
  })().finally(() => {
    refreshPromise = null;
  });
  return refreshPromise;
}

async function _read<T>(res: Response): Promise<T> {
  const text = await res.text();
  const data = text ? safeJson(text) : null;
  return data as T;
}

async function _throwApiError(res: Response): Promise<never> {
  const text = await res.text();
  const data = text ? safeJson(text) : null;
  let detail: unknown;
  let validationDetails: unknown;
  if (data && typeof data === "object") {
    const obj = data as Record<string, unknown>;
    // The backend wraps errors as `{ error: { code, message, request_id,
    // details? } }`. Read that first, then fall back to FastAPI's default
    // `{ detail }` and a bare top-level `{ message }` for robustness.
    const envelope =
      obj.error && typeof obj.error === "object"
        ? (obj.error as Record<string, unknown>)
        : null;
    detail = envelope?.message ?? obj.detail ?? obj.message;
    validationDetails = envelope?.code === "validation_error" ? envelope.details : detail;
  }
  const message = validationMessage(validationDetails) || detail || res.statusText || "Request failed";
  const err: ApiError = { status: res.status, message: String(message), ...detailReason(data) };
  throw err;
}

// Both the production error envelope and FastAPI's default validation shape
// contain field paths/messages. Render those instead of "[object Object]".
function validationMessage(details: unknown): string | null {
  if (!Array.isArray(details)) return null;
  const messages = details.flatMap((issue: unknown) => {
    if (!issue || typeof issue !== "object" || !("msg" in issue) || typeof issue.msg !== "string") return [];
    const loc = "loc" in issue && Array.isArray(issue.loc) ? issue.loc.map(String).join(".") : "";
    return [loc ? `${loc}: ${issue.msg}` : issue.msg];
  });
  return messages.join("; ") || null;
}

// A structured HTTPException detail (`{"error": "<reason>", "message": …}`)
// lands in the envelope's `error.details`. Surface its `error` key as
// `reason` so callers can tell one gate from another (Plan 31's
// `scenarios_disabled`) without matching on message text.
function detailReason(data: unknown): { reason?: string } {
  if (!data || typeof data !== "object" || !("error" in data)) return {};
  const envelope = data.error;
  if (!envelope || typeof envelope !== "object" || !("details" in envelope)) {
    return {};
  }
  const details = envelope.details;
  return details &&
    typeof details === "object" &&
    "error" in details &&
    typeof details.error === "string"
    ? { reason: details.error }
    : {};
}

async function request<T>(
  path: string,
  init: RequestInit,
): Promise<T> {
  let res = await _fetch(path, init);
  if (res.status === 401 && _authRefreshable(path)) {
    const refreshed = await _refreshSession();
    if (refreshed) {
      const method = (init.method || "GET").toUpperCase();
      if (method === "GET" || method === "HEAD") {
        // Safe to transparently replay an idempotent request after refresh.
        res = await _fetch(path, init);
      } else {
        // Do NOT auto-replay a non-idempotent mutation: it may already have
        // been applied server-side before the 401, so replaying could
        // double-submit (e.g. create a duplicate). The session is valid again
        // (refresh succeeded), so surface a retriable error WITHOUT clearing
        // auth — the caller re-issues intentionally.
        const err: ApiError = {
          status: 401,
          message: "Your session was refreshed — please retry.",
        };
        throw err;
      }
    }
  }
  if (!res.ok) {
    if (res.status === 401) _handle401();
    await _throwApiError(res);
  }
  return _read<T>(res);
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export type HealthOut = {
  status: "ok";
  db: boolean;
  worker_seen_within_seconds: number | null;
};

export type Role = "supadmin" | "admin" | "manager";

export type UserMembership = {
  community_name: string;
  team_name: string | null;
};

export type UserOut = {
  id: string;
  email: string;
  full_name: string | null;
  first_name: string | null;
  last_name: string | null;
  role: Role;
  is_active: boolean;
  created_at: string;
  last_login_at: string | null;
  memberships: UserMembership[];
};

export type CreateUserIn = {
  first_name: string;
  last_name: string;
  email: string;
  role: Role;
  community_ids?: string[];
};

export type UpdateUserIn = {
  first_name?: string;
  last_name?: string;
  role?: Role;
  is_active?: boolean;
};

export type JobStatus = "pending" | "running" | "succeeded" | "failed" | "dead";

export type JobOut = {
  id: string;
  kind: string;
  status: JobStatus;
  attempts: number;
  max_attempts: number;
  next_run_at: string;
  locked_by: string | null;
  last_error: string | null;
  created_at: string;
  completed_at: string | null;
};

export const api = {
  // Sign-in is an Azure SSO redirect (see azureLoginUrl); there is no
  // password login endpoint. Logout revokes the session + clears cookies.
  logout: () =>
    request<void>("/auth/logout", { method: "POST" }),
  health: () =>
    request<HealthOut>("/healthz", { method: "GET" }),
  me: () =>
    request<UserOut>("/auth/me", { method: "GET" }),
  users: () =>
    request<UserOut[]>("/users", { method: "GET" }),
  createUser: (body: CreateUserIn) =>
    request<UserOut>("/users", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateUser: (userId: string, body: UpdateUserIn) =>
    request<UserOut>(`/users/${userId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  deleteUser: (userId: string) =>
    request<void>(`/users/${userId}`, { method: "DELETE" }),
  jobs: (status?: JobStatus) =>
    request<JobOut[]>(
      `/jobs${status ? `?status=${status}` : ""}`,
      { method: "GET" },
    ),
  jobStatus: (jobId: string) =>
    request<JobOut>(
      `/jobs/${encodeURIComponent(jobId)}`,
      { method: "GET" },
    ),
  dialpadPreview: (start: string, end: string) =>
    request<DialpadPreview>("/dialpad/preview", {
      method: "POST",
      body: JSON.stringify({ start, end }),
    }),
  dialpadIngest: (
    start: string,
    end: string,
    force: boolean = false,
    phases: "all" | "phase1" | "phase2" | "phase3" = "all",
  ) =>
    request<DialpadIngestResult>("/dialpad/ingest", {
      method: "POST",
      body: JSON.stringify({ start, end, force, phases }),
    }),
  dialpadWindows: () =>
    request<DialpadSyncWindow[]>("/dialpad/windows", { method: "GET" }),
  dialpadActive: () =>
    request<DialpadActive>("/dialpad/active", { method: "GET" }),
  dialpadClearData: () =>
    request<void>("/dialpad/data", { method: "DELETE" }),
  dialpadCoverage: () =>
    request<DialpadCoverage>("/dialpad/coverage", { method: "GET" }),
  dialpadSchedules: () =>
    request<SyncSchedule[]>("/dialpad/schedules", { method: "GET" }),
  dialpadCreateSchedule: (body: {
    label?: string;
    time_of_day: string;
    days_of_week?: number[];
  }) =>
    request<SyncSchedule>("/dialpad/schedules", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  dialpadUpdateSchedule: (
    id: string,
    body: {
      label?: string;
      time_of_day?: string;
      days_of_week?: number[];
      is_enabled?: boolean;
    },
  ) =>
    request<SyncSchedule>(`/dialpad/schedules/${encodeURIComponent(id)}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  dialpadDeleteSchedule: (id: string) =>
    request<void>(`/dialpad/schedules/${encodeURIComponent(id)}`, {
      method: "DELETE",
    }),
  dialpadSyncUsers: () =>
    request<{ job_id: string }>("/dialpad/sync-users", {
      method: "POST",
    }),
  dialpadSyncUsersActive: () =>
    request<{
      has_active: boolean;
      job_id: string | null;
      status: string | null;
      queued_at: string | null;
      failed: boolean;
      error: string | null;
    }>("/dialpad/sync-users/active", { method: "GET" }),
  dialpadRetryMissingTranscripts: () =>
    request<{ job_id: string }>("/dialpad/retry-missing-transcripts", {
      method: "POST",
    }),
  dialpadRetryMissingTranscriptsActive: () =>
    request<DialpadRetryMissingTranscriptsActive>(
      "/dialpad/retry-missing-transcripts/active",
      { method: "GET" },
    ),
  dialpadRetryFailedAssessments: () =>
    request<{ run_id: string }>("/dialpad/retry-failed-assessments", {
      method: "POST",
    }),
  dialpadUsers: (limit: number = 500) =>
    request<DialpadUsersResponse>(`/dialpad/users?limit=${limit}`, {
      method: "GET",
    }),
  dialpadDownloadGroupsCsv: async () => {
    const url = `${BACKEND}/dialpad/groups/download`;
    const res = await fetch(url, {
      method: "GET",
      credentials: "include",
    });
    if (!res.ok) {
      const text = await res.text();
      let message = res.statusText || "Download failed";
      try {
        const data = JSON.parse(text);
        message = data?.detail || data?.message || message;
      } catch {
        if (text) message = text;
      }
      const err: ApiError = { status: res.status, message: String(message) };
      throw err;
    }
    const today = new Date().toISOString().slice(0, 10);
    let filename = `dialpad-groups-${today}.csv`;
    const cd = res.headers.get("Content-Disposition");
    if (cd) {
      const m =
        /filename\*=UTF-8''([^;]+)/i.exec(cd) ||
        /filename="?([^";]+)"?/i.exec(cd);
      if (m && m[1]) {
        try {
          filename = decodeURIComponent(m[1]);
        } catch {
          filename = m[1];
        }
      }
    }
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    try {
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  },
  dialpadSettings: () =>
    request<DialpadSettings>("/settings/dialpad", { method: "GET" }),
  setDialpadSettings: (min_transcript_duration_ms: number) =>
    request<DialpadSettings>("/settings/dialpad", {
      method: "PUT",
      body: JSON.stringify({ min_transcript_duration_ms }),
    }),
  setDialpadSyncButtonsDisabled: (sync_buttons_disabled: boolean) =>
    request<DialpadSettings>("/settings/dialpad/sync-buttons-disabled", {
      method: "PUT",
      body: JSON.stringify({ sync_buttons_disabled }),
    }),
  // ADR 0036 — retention status for the Data panel, plus the runtime brake.
  // `enabled` is deploy-gated and read-only here; only `paused` is writable.
  retentionSettings: () =>
    request<RetentionSettings>("/settings/retention", { method: "GET" }),
  setRetentionPaused: (paused: boolean) =>
    request<RetentionSettings>("/settings/retention/paused", {
      method: "PUT",
      body: JSON.stringify({ paused }),
    }),
  // PET-641 / PET-672 — admin-curated suggested questions for the chat
  // surfaces. The default read is open to any authenticated user and is
  // team-scoped to the caller (unassigned questions are never returned).
  // Pass management=true for the admin management UI to get the full,
  // unfiltered list. The write endpoint is admin/supadmin-only.
  suggestedQuestions: (management = false) =>
    request<SuggestedQuestionsOut>(
      management ? "/settings/questions?management=true" : "/settings/questions",
      { method: "GET" },
    ),
  setSuggestedQuestions: (questions: SuggestedQuestion[]) =>
    request<SuggestedQuestionsOut>("/settings/questions", {
      method: "PUT",
      body: JSON.stringify({ questions }),
    }),
  agents: (
    teamId?: string | null,
    timeRange: TimeRange = "all_time",
    rangeMode: RangeMode = "calendar",
    customRange?: CustomDateRange | null,
  ) => {
    const params = new URLSearchParams();
    if (teamId) params.set("team_id", teamId);
    params.set("time_range", timeRange);
    // Daily / All-time / Custom ignore range_mode (matches the
    // dashboard's behaviour); normalise so the cache key stays stable.
    const effectiveMode =
      timeRange === "daily" ||
      timeRange === "all_time" ||
      timeRange === "custom"
        ? "calendar"
        : rangeMode;
    params.set("range_mode", effectiveMode);
    if (timeRange === "custom" && customRange) {
      params.set("start", customRange.start);
      params.set("end", customRange.end);
    }
    return request<AgentRow[]>(
      `/dialpad/agents?${params.toString()}`,
      { method: "GET" },
    );
  },
  agentProfile: (
    agentId: string,
    teamId?: string | null,
    timeRange: TimeRange = "all_time",
    rangeMode: RangeMode = "calendar",
    customRange?: CustomDateRange | null,
  ) => {
    const params = new URLSearchParams();
    if (teamId) params.set("team_id", teamId);
    params.set("time_range", timeRange);
    const effectiveMode =
      timeRange === "daily" ||
      timeRange === "all_time" ||
      timeRange === "custom"
        ? "calendar"
        : rangeMode;
    params.set("range_mode", effectiveMode);
    if (timeRange === "custom" && customRange) {
      params.set("start", customRange.start);
      params.set("end", customRange.end);
    }
    return request<AgentProfile>(
      `/dialpad/agents/${encodeURIComponent(agentId)}?${params.toString()}`,
      { method: "GET" },
    );
  },
  // Task #349 — derive a practical training module from a Training
  // Tips report. Stateless: caller passes only the ``report_id``
  // returned by the Training Tips endpoint; the server resolves the
  // original report text from its in-memory cache.
  generateTrainingModule: (
    body: { report_id: string },
  ) =>
    request<{ html: string }>("/assessments/training-module/generate", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  // Task #279 — on-demand Training Tips report (no persistence).
  // PET-656 — optional admin-only ``max_calls`` override; only included
  // in the request body when non-null (the server clamps it regardless).
  trainingTipsForAgent: (
    agentId: string,
    body: {
      from: string | null;
      to: string;
      labels?: string[];
      max_calls?: number | null;
    },
  ) =>
    request<TrainingTipsOut>(
      `/assessments/training-tips/${encodeURIComponent(agentId)}`,
      {
        method: "POST",
        body: JSON.stringify({
          from: body.from,
          to: body.to,
          labels: body.labels ?? [],
          ...(body.max_calls != null ? { max_calls: body.max_calls } : {}),
        }),
      },
    ),
  // ---------- Assessments Ticket 11/12/13 — aggregates + per-agent results ----------
  aggregateForTeam: (
    teamId: string,
    params: {
      from: string;
      to: string;
      assessment_id?: string | null;
      // Task #226 — optional paired prior window so the single-team
      // Assessment performance card on the Team Profile can render
      // a "↑/↓ pts vs prior" chip + per-bar prior markers (mirrors
      // the Agent Profile card). Passing only one half is rejected
      // server-side (422).
      prior_from?: string | null;
      prior_to?: string | null;
      // Task #302 — optional global Agent filter. When set every
      // metric on the team card is recomputed from that agent's
      // calls only. An agent outside the team's roster yields an
      // empty (zero-call) aggregate so the FE renders the empty
      // state instead of stale numbers.
      agent_id?: string | null;
    },
  ) => {
    const qs = new URLSearchParams({ from: params.from, to: params.to });
    if (params.assessment_id) qs.set("assessment_id", params.assessment_id);
    if (params.prior_from) qs.set("prior_from", params.prior_from);
    if (params.prior_to) qs.set("prior_to", params.prior_to);
    if (params.agent_id) qs.set("agent_id", params.agent_id);
    return request<TeamAggregate>(
      `/assessments/aggregates/team/${encodeURIComponent(teamId)}?${qs.toString()}`,
      { method: "GET" },
    );
  },
  aggregateForAgent: (
    agentId: string,
    params: {
      // Task #246 — ``from`` is nullable so the Agent View's "All time"
      // option can request an unbounded window (every scored call the
      // agent has ever had). The route accepts a missing ``from`` and
      // skips the lower-bound filter; ``to`` is still required.
      from: string | null;
      to: string;
      assessment_id?: string | null;
      // Task #220 — optional paired prior window so the Agent Profile
      // summary strip can render a "↑/↓ pts vs prior" chip and the
      // MeasureBarChart can paint per-bar prior markers. Passing only
      // one half is rejected server-side (422).
      prior_from?: string | null;
      prior_to?: string | null;
    },
  ) => {
    const qs = new URLSearchParams({ to: params.to });
    if (params.from) qs.set("from", params.from);
    if (params.assessment_id) qs.set("assessment_id", params.assessment_id);
    if (params.prior_from) qs.set("prior_from", params.prior_from);
    if (params.prior_to) qs.set("prior_to", params.prior_to);
    return request<AgentAggregate>(
      `/assessments/aggregates/agent/${encodeURIComponent(agentId)}?${qs.toString()}`,
      { method: "GET" },
    );
  },
  // Ticket 18 — by-assessment aggregates feed the Teams view's
  // "By assessment" mode. Server scopes the response to the requester's
  // visibility (Ticket 17), so the client just renders whatever lands.
  listByAssessmentAggregates: (
    params: {
      from: string;
      to: string;
      // Task #211 — optional paired prior window so the FE
      // by-assessment cards can render the same prior-period tick
      // the by-team triage list already shows. Passing only one
      // half is rejected server-side (422).
      prior_from?: string | null;
      prior_to?: string | null;
      // Task #302 — optional global Team + Agent filters from the
      // Teams view. Both scope every score / call count /
      // per-label row in the response; assessments with zero
      // in-scope graded calls are dropped server-side.
      team_id?: string | null;
      agent_id?: string | null;
    },
  ) => {
    const qs = new URLSearchParams({ from: params.from, to: params.to });
    if (params.prior_from) qs.set("prior_from", params.prior_from);
    if (params.prior_to) qs.set("prior_to", params.prior_to);
    if (params.team_id) qs.set("team_id", params.team_id);
    if (params.agent_id) qs.set("agent_id", params.agent_id);
    return request<AssessmentAggregate[]>(
      `/assessments/aggregates/by-assessment?${qs.toString()}`,
      { method: "GET" },
    );
  },
  // Task #167 — daily graded-call counts for a single assessment, used
  // by the trend-line chart inside each by-assessment card.
  byAssessmentTrend: (
    assessmentId: string,
    params: {
      from: string;
      to: string;
      // Task #302 — optional global Team + Agent filters from the
      // Teams view. When set, the per-day counts only reflect
      // that scope so the trend matches the rest of the card.
      team_id?: string | null;
      agent_id?: string | null;
    },
  ) => {
    const qs = new URLSearchParams({ from: params.from, to: params.to });
    if (params.team_id) qs.set("team_id", params.team_id);
    if (params.agent_id) qs.set("agent_id", params.agent_id);
    return request<AssessmentTrendPoint[]>(
      `/assessments/aggregates/by-assessment/${encodeURIComponent(
        assessmentId,
      )}/trend?${qs.toString()}`,
      { method: "GET" },
    );
  },
  // Task #196 — optional ``assessment_id`` filter so the team trend
  // can follow the Teams view's active assessment dropdown
  // (single assessment ⇒ that assessment's detection rate; null ⇒
  // every assessment the team's agents have detections for).
  byTeamTrend: (
    teamId: string,
    params: {
      from: string;
      to: string;
      assessment_id?: string | null;
      // Task #197 — focused measure follows the same (measure, label)
      // selection driving the agent drill so the trend line reshapes
      // to the bar the user clicked.
      measure_id?: string | null;
      label_name?: string | null;
      // Task #302 — optional global Agent filter from the Teams
      // View. When set the per-day counts only reflect that agent's
      // calls so the team-trend line matches the rest of the card.
      agent_id?: string | null;
    },
  ) => {
    const qs = new URLSearchParams({ from: params.from, to: params.to });
    if (params.assessment_id) qs.set("assessment_id", params.assessment_id);
    if (params.measure_id && params.label_name) {
      qs.set("measure_id", params.measure_id);
      qs.set("label_name", params.label_name);
    }
    if (params.agent_id) qs.set("agent_id", params.agent_id);
    return request<AssessmentTrendPoint[]>(
      `/assessments/aggregates/team/${encodeURIComponent(
        teamId,
      )}/trend?${qs.toString()}`,
      { method: "GET" },
    );
  },
  // Task #197 — triage list (per-team current + prior aggregates so the
  // Teams view can sort by "needs attention" and render inline
  // prior-period comparisons without an N-team fan-out).
  teamsTriage: (
    params: {
      from: string;
      to: string;
      prior_from?: string | null;
      prior_to?: string | null;
      assessment_id?: string | null;
      team_ids?: string[] | null;
      // Task #302 — global Agent filter from the Teams view.
      // When set every triage row is computed only from that agent's
      // calls; teams the agent isn't on render an empty (zero-call)
      // row server-side.
      agent_id?: string | null;
    },
  ) => {
    const qs = new URLSearchParams({ from: params.from, to: params.to });
    if (params.prior_from) qs.set("prior_from", params.prior_from);
    if (params.prior_to) qs.set("prior_to", params.prior_to);
    if (params.assessment_id) qs.set("assessment_id", params.assessment_id);
    if (params.team_ids && params.team_ids.length > 0) {
      for (const tid of params.team_ids) qs.append("team_ids", tid);
    }
    if (params.agent_id) qs.set("agent_id", params.agent_id);
    return request<TeamTriageRow[]>(
      `/assessments/aggregates/teams-triage?${qs.toString()}`,
      { method: "GET" },
    );
  },
  // Task #197 — agents on one team ranked by a focused measure (or the
  // overall detection rate when nothing is focused). Powers the inline
  // agent drill on each triage row.
  teamAgentsByMeasure: (
    teamId: string,
    params: {
      from: string;
      to: string;
      assessment_id?: string | null;
      measure_id?: string | null;
      label_name?: string | null;
    },
  ) => {
    const qs = new URLSearchParams({ from: params.from, to: params.to });
    if (params.assessment_id) qs.set("assessment_id", params.assessment_id);
    if (params.measure_id && params.label_name) {
      qs.set("measure_id", params.measure_id);
      qs.set("label_name", params.label_name);
    }
    return request<AgentMeasureRow[]>(
      `/assessments/aggregates/team/${encodeURIComponent(
        teamId,
      )}/agents-by-measure?${qs.toString()}`,
      { method: "GET" },
    );
  },
  // Task #286 — per-call assessment results for a single team. Mirrors
  // ``assessmentResultsByAgent`` so the Teams view "By Assessment"
  // view can render a focused calls list under each team.
  assessmentResultsByTeam: (
    teamId: string,
    params: { from: string | null; to: string; assessment_id?: string | null },
  ) => {
    const qs = new URLSearchParams({ to: params.to });
    if (params.from) qs.set("from", params.from);
    if (params.assessment_id) qs.set("assessment_id", params.assessment_id);
    return request<AgentAssessmentResultRow[]>(
      `/assessments/results/by-team/${encodeURIComponent(teamId)}?${qs.toString()}`,
      { method: "GET" },
    );
  },
  assessmentResultsByAgent: (
    agentId: string,
    // Task #246 — ``from`` is nullable to match ``aggregateForAgent``
    // so the Agent View's "All time" option requests every scored
    // call on record (no lower-bound filter applied server-side).
    params: { from: string | null; to: string; assessment_id?: string | null },
  ) => {
    const qs = new URLSearchParams({ to: params.to });
    if (params.from) qs.set("from", params.from);
    if (params.assessment_id) qs.set("assessment_id", params.assessment_id);
    return request<AgentAssessmentResultRow[]>(
      `/assessments/results/by-agent/${encodeURIComponent(agentId)}?${qs.toString()}`,
      { method: "GET" },
    );
  },
  agentCalls: (
    agentId: string,
    opts?: {
      teamId?: string | null;
      slice?: AgentCallSlice | null;
      labels?: string[];
      timeRange?: TimeRange;
      rangeMode?: RangeMode;
      customRange?: CustomDateRange | null;
    },
  ) => {
    const qs = new URLSearchParams();
    if (opts?.teamId) qs.set("team_id", opts.teamId);
    if (opts?.slice) qs.set("slice", opts.slice);
    const timeRange: TimeRange = opts?.timeRange ?? "all_time";
    const rangeMode: RangeMode = opts?.rangeMode ?? "calendar";
    qs.set("time_range", timeRange);
    qs.set(
      "range_mode",
      timeRange === "daily" ||
      timeRange === "all_time" ||
      timeRange === "custom"
        ? "calendar"
        : rangeMode,
    );
    if (timeRange === "custom" && opts?.customRange) {
      qs.set("start", opts.customRange.start);
      qs.set("end", opts.customRange.end);
    }
    for (const k of opts?.labels ?? []) qs.append("labels", k);
    const tail = qs.toString();
    return request<AgentCallsOut>(
      `/dialpad/agents/${encodeURIComponent(agentId)}/calls${tail ? `?${tail}` : ""}`,
      { method: "GET" },
    );
  },
  callDetail: (callId: string) =>
    request<CallDetail>(
      `/dialpad/calls/${encodeURIComponent(callId)}`,
      { method: "GET" },
    ),
  // Task #331 — Search page phone-number search + per-number history.
  // T12 — the URL surface moved from /dialpad/clients/* to
  // /dialpad/search/*; method names keep the "client" domain vocabulary
  // (the search subject is still a client phone number).
  clientPhoneSuggestions: (q: string) =>
    request<ClientPhoneSuggestionsOut>(
      `/dialpad/search/phone-suggestions?q=${encodeURIComponent(q)}`,
      { method: "GET" },
    ),
  // Task #350 — Search page agent-name suggestions and per-agent call
  // history (companion to the existing phone-number endpoints).
  clientAgentSuggestions: (q: string) =>
    request<ClientAgentSuggestionsOut>(
      `/dialpad/search/agent-suggestions?q=${encodeURIComponent(q)}`,
      { method: "GET" },
    ),
  clientAgentCalls: (agentId: string) =>
    request<ClientCallsOut>(
      `/dialpad/search/agent-calls?agent_id=${encodeURIComponent(agentId)}`,
      { method: "GET" },
    ),
  clientCalls: (phone: string) =>
    request<ClientCallsOut>(
      `/dialpad/search/calls?phone=${encodeURIComponent(phone)}`,
      { method: "GET" },
    ),
  // Task #336 — "Ask a Question" chat on the Search page.
  clientQa: (body: ClientQaIn) =>
    request<ClientQaOut>("/dialpad/search/qa", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  // Wave 5 · T1 — Roundtable multi-agent comparison Q&A. One question
  // per request: the server answers per panel agent (grounded in that
  // agent's ≤100 most recent in-scope calls) and rolls the answers into
  // a single comparison takeaway. Session-only — nothing persists.
  roundtableQa: (body: RoundtableQaIn) =>
    request<RoundtableQaOut>("/dialpad/roundtable/qa", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  refreshCallAssessmentSummary: (callId: string) =>
    request<{ job_id: string | null }>(
      `/dialpad/calls/${encodeURIComponent(callId)}/assessment-summary/refresh`,
      { method: "POST" },
    ),
  myTeams: () =>
    request<MyTeam[]>("/teams/mine", { method: "GET" }),

  // -- Communities (SupaAdmin) --
  listCommunities: () =>
    request<CommunityOut[]>("/communities", { method: "GET" }),
  createCommunity: (body: CommunityIn) =>
    request<CommunityOut>("/communities", {
      method: "POST", body: JSON.stringify(body),
    }),
  updateCommunity: (id: string, body: CommunityIn) =>
    request<CommunityOut>(`/communities/${id}`, {
      method: "PUT", body: JSON.stringify(body),
    }),
  deleteCommunity: (id: string) =>
    request<void>(`/communities/${id}`, { method: "DELETE" }),

  // -- Teams (Admin) --
  listTeams: (communityId?: string) =>
    request<TeamOut[]>(
      `/teams${communityId ? `?community_id=${encodeURIComponent(communityId)}` : ""}`,
      { method: "GET" },
    ),
  createTeam: (body: TeamIn) =>
    request<TeamOut>("/teams", {
      method: "POST", body: JSON.stringify(body),
    }),
  updateTeam: (id: string, body: TeamIn) =>
    request<TeamOut>(`/teams/${id}`, {
      method: "PUT", body: JSON.stringify(body),
    }),
  deleteTeam: (id: string) =>
    request<void>(`/teams/${id}`, { method: "DELETE" }),
  // Team business policy: scoped admin reads, super-admin revision-protected writes.
  scenarioSettings: (id: string) =>
    request<ScenarioSettingsOut>(`/teams/${encodeURIComponent(id)}/scenario-settings`, { method: "GET" }),
  saveScenarioSettings: (id: string, body: ScenarioSettingsUpdate) =>
    request<ScenarioSettingsOut>(`/teams/${encodeURIComponent(id)}/scenario-settings`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  // Legacy single-flag endpoint remains available to existing callers.
  setScenarioAvailability: (id: string, scenariosEnabled: boolean) =>
    request<TeamOut>(`/teams/${encodeURIComponent(id)}/scenario-availability`, {
      method: "PATCH",
      body: JSON.stringify({ scenarios_enabled: scenariosEnabled }),
    }),
  scopedCommunities: () =>
    request<AdminScopedCommunity[]>("/teams/scope/communities", {
      method: "GET",
    }),
  unassignedCalls: (communityId: string) =>
    request<UnassignedCall[]>(
      `/teams/unassigned-calls/${encodeURIComponent(communityId)}`,
      { method: "GET" },
    ),
  analyticsDashboard: (
    teamId?: string | null,
    timeRange: TimeRange = "all_time",
    compare: boolean = false,
    agentId?: string | null,
    rangeMode: RangeMode = "calendar",
    customRange?: CustomDateRange | null,
  ) => {
    const params = new URLSearchParams();
    if (teamId) params.set("team_id", teamId);
    if (agentId) params.set("agent_id", agentId);
    params.set("time_range", timeRange);
    // range_mode now governs both the current and prior period, so it
    // always belongs on the request — not just when compare is on.
    params.set("range_mode", rangeMode);
    if (timeRange === "custom" && customRange) {
      params.set("start", customRange.start);
      params.set("end", customRange.end);
    }
    if (compare) params.set("compare", "true");
    // Pass the browser's IANA timezone so the hourly chart buckets by
    // the user's local hour-of-day instead of UTC (a 4pm Pacific call
    // would otherwise land in the midnight bar).
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (tz) params.set("tz", tz);
    } catch {
      // Older runtime without Intl support — server defaults to UTC.
    }
    return request<AnalyticsDashboard>(
      `/analytics/dashboard?${params.toString()}`,
      { method: "GET" },
    );
  },
  listAssessments: () =>
    request<AssessmentRow[]>("/assessments", { method: "GET" }),
  createAssessment: (body: AssessmentInput) =>
    request<AssessmentDetail>("/assessments", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  getAssessment: (id: string) =>
    request<AssessmentDetail>(`/assessments/${encodeURIComponent(id)}`, {
      method: "GET",
    }),
  // Read-only access for admins & managers — scoped server-side to
  // assessments assigned to teams within the actor's visibility. Super
  // admins keep using listAssessments / getAssessment (the full editor
  // data).
  listAssessmentsScoped: () =>
    request<AssessmentRow[]>("/assessments/scoped", { method: "GET" }),
  getAssessmentReadOnly: (id: string) =>
    request<AssessmentReadOnlyDetail>(
      `/assessments/scoped/${encodeURIComponent(id)}`,
      { method: "GET" },
    ),
  updateAssessment: (
    id: string,
    patch: AssessmentPatch,
    ifMatchVersion: number,
  ) =>
    request<AssessmentDetail & { unassigned_teams: AssessmentTeamRef[] }>(
      `/assessments/${encodeURIComponent(id)}`,
      {
        method: "PATCH",
        body: JSON.stringify(patch),
        headers: { "If-Match": String(ifMatchVersion) },
      },
    ),
  assessmentChanges: (
    id: string,
    limit: number = 20,
    offset: number = 0,
  ) =>
    request<AssessmentChangeLogPage>(
      `/assessments/${encodeURIComponent(id)}/changes?limit=${limit}&offset=${offset}`,
      { method: "GET" },
    ),
  previewDeleteAssessment: (id: string) =>
    request<{ teams_to_unassign: AssessmentTeamRef[]; result_count: number }>(
      `/assessments/${encodeURIComponent(id)}/preview-delete`,
      { method: "POST" },
    ),
  deleteAssessment: (id: string) =>
    request<{ id: string; deleted_teams: AssessmentTeamRef[] }>(
      `/assessments/${encodeURIComponent(id)}`,
      { method: "DELETE" },
    ),
  listAssignableAssessments: () =>
    request<AssignableAssessment[]>("/assessments/assignable", {
      method: "GET",
    }),
  getTeamAssessments: (teamId: string) =>
    request<AssessmentTeamRef[]>(
      `/teams/${encodeURIComponent(teamId)}/assessments`,
      { method: "GET" },
    ),
  setTeamAssessments: (teamId: string, assessmentIds: string[]) =>
    request<AssessmentTeamRef[]>(
      `/teams/${encodeURIComponent(teamId)}/assessments`,
      {
        method: "PUT",
        body: JSON.stringify({ assessment_ids: assessmentIds }),
      },
    ),
  setAssessmentTeams: (
    assessmentId: string,
    teamIds: string[],
  ) =>
    request<{ id: string; name: string; community_name: string }[]>(
      `/assessments/${encodeURIComponent(assessmentId)}/teams`,
      {
        method: "PUT",
        body: JSON.stringify({ team_ids: teamIds }),
      },
    ),
  previewAssessmentImpact: (
    id: string,
    draftMeasures: MeasureInput[],
  ) =>
    request<{
      would_revert_to_draft: boolean;
      teams_to_unassign: AssessmentTeamRef[];
    }>(`/assessments/${encodeURIComponent(id)}/preview-impact`, {
      method: "POST",
      body: JSON.stringify({ measures: draftMeasures }),
    }),
  // ---------- Assessments Ticket 8 — Phase 3 LLM runs ----------
  assessmentRunsAssignableTeams: () =>
    request<AssessmentRunAssignableTeam[]>(
      "/assessments/runs/assignable-teams",
      { method: "GET" },
    ),
  assessmentRunsAllowedModels: () =>
    request<AssessmentRunAllowedModels>(
      "/assessments/runs/allowed-models",
      { method: "GET" },
    ),
  assessmentRunsSetDefaultModel: (model: string) =>
    request<{ current: string }>("/assessments/runs/default-model", {
      method: "PUT",
      body: JSON.stringify({ model }),
    }),
  // Ticket 15 — read/write the persisted per-job concurrency cap.
  assessmentRunSettings: () =>
    request<AssessmentRunSettings>(
      "/assessments/runs/settings",
      { method: "GET" },
    ),
  assessmentRunSettingsUpdate: (
    body: { concurrency: number },
  ) =>
    request<AssessmentRunSettings>(
      "/assessments/runs/settings",
      {
        method: "PUT",
        body: JSON.stringify(body),
      },
    ),
  assessmentRunsScan: (body: AssessmentRunScanIn) =>
    request<AssessmentRunScanOut>("/assessments/runs/scan", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  assessmentRunsEnqueue: (body: AssessmentRunScanIn) =>
    request<{ run_id: string; status: "queued" }>("/assessments/runs", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  assessmentRunsActive: () =>
    request<{ has_active: boolean; run_id: string | null }>(
      "/assessments/runs/active",
      { method: "GET" },
    ),
  assessmentRunStatus: (runId: string) =>
    request<AssessmentRunStatus>(
      `/assessments/runs/${encodeURIComponent(runId)}/status`,
      { method: "GET" },
    ),
  assessmentRunCancel: (runId: string) =>
    request<{ run_id: string; status: "cancelled" }>(
      `/assessments/runs/${encodeURIComponent(runId)}/cancel`,
      { method: "POST" },
    ),
  assessmentRunRetryFailed: (runId: string) =>
    request<{ run_id: string; status: "queued" }>(
      `/assessments/runs/${encodeURIComponent(runId)}/retry-failed`,
      { method: "POST" },
    ),
  assessmentRunByWindow: (syncWindowId: string) =>
    request<AssessmentRunStatus | null>(
      `/assessments/runs/by-window/${encodeURIComponent(syncWindowId)}`,
      { method: "GET" },
    ),
  listTeamAgents: (teamId: string) =>
    request<AnalyticsTeamAgent[]>(
      `/analytics/teams/${encodeURIComponent(teamId)}/agents`,
      { method: "GET" },
    ),
  assessmentRunsLatest: () =>
    request<AssessmentRunStatus | null>(
      `/assessments/runs/latest`,
      { method: "GET" },
    ),
  assessmentRunsList: (limit = 10, offset = 0) =>
    request<{
      items: AssessmentRunStatus[];
      has_more: boolean;
      limit: number;
      offset: number;
    }>(
      `/assessments/runs?limit=${limit}&offset=${offset}`,
      { method: "GET" },
    ),
  // ---------- Assessments Ticket 9 — Delete Results dev tool ----------
  previewDeleteResults: (body: DeleteResultsBody) =>
    request<DeleteResultsPreview>(
      "/assessments/results/preview-delete",
      { method: "POST", body: JSON.stringify(body) },
    ),
  deleteResults: (body: DeleteResultsBody) =>
    request<DeleteResultsOut>("/assessments/results", {
      method: "DELETE",
      body: JSON.stringify(body),
    }),
  assessmentRunFailedPairs: (
    runId: string,
    limit: number = 50,
    offset: number = 0,
  ) => {
    const params = new URLSearchParams();
    params.set("limit", String(limit));
    params.set("offset", String(offset));
    return request<AssessmentRunFailedPairsPage>(
      `/assessments/runs/${encodeURIComponent(runId)}/failed-pairs?${params.toString()}`,
      { method: "GET" },
    );
  },
  analyticsTeamAgents: (teamId: string) =>
    request<AnalyticsTeamAgent[]>(
      `/analytics/teams/${encodeURIComponent(teamId)}/agents`,
      { method: "GET" },
    ),
  analyticsTeamSentimentBreakdown: (
    teamId: string,
    timeRange: TimeRange = "all_time",
    rangeMode: RangeMode = "calendar",
    agentId?: string | null,
    customRange?: CustomDateRange | null,
  ) => {
    const params = new URLSearchParams();
    params.set("time_range", timeRange);
    params.set("range_mode", rangeMode);
    if (agentId) params.set("agent_id", agentId);
    if (timeRange === "custom" && customRange) {
      params.set("start", customRange.start);
      params.set("end", customRange.end);
    }
    return request<AnalyticsTeamAgentSentimentRow[]>(
      `/analytics/teams/${encodeURIComponent(teamId)}/sentiment-breakdown?${params.toString()}`,
      { method: "GET" },
    );
  },
  // Task #385 — Dialpad call-sync reconciliation
  dialpadReconciliationRuns: (
    limit: number = 20,
    offset: number = 0,
  ) =>
    request<DialpadReconciliationRunList>(
      `/dialpad/reconciliation/runs?limit=${limit}&offset=${offset}`,
      { method: "GET" },
    ),
  dialpadReconciliationRun: (runId: string) =>
    request<DialpadReconciliationRunDetail>(
      `/dialpad/reconciliation/runs/${encodeURIComponent(runId)}`,
      { method: "GET" },
    ),
  createDialpadReconciliationRun: (
    body: DialpadReconciliationRunCreateBody,
  ) =>
    request<DialpadReconciliationRunSummary>(
      `/dialpad/reconciliation/runs`,
      { method: "POST", body: JSON.stringify(body) },
    ),
  dialpadReconciliationCheck: (
    runId: string,
    severities?: DialpadReconciliationSeverity[],
  ) =>
    request<DialpadReconciliationActionOut>(
      `/dialpad/reconciliation/runs/${encodeURIComponent(runId)}/check`,
      {
        method: "POST",
        body: JSON.stringify({ severities: severities ?? null }),
      },
    ),
  dialpadReconciliationCorrect: (runId: string) =>
    request<DialpadReconciliationActionOut>(
      `/dialpad/reconciliation/runs/${encodeURIComponent(runId)}/correct`,
      { method: "POST" },
    ),
  dialpadReconciliationCancel: (runId: string) =>
    request<DialpadReconciliationActionOut>(
      `/dialpad/reconciliation/runs/${encodeURIComponent(runId)}/cancel`,
      { method: "POST" },
    ),
  dialpadReconciliationCheckAndRectify: (
    body: DialpadReconciliationRunCreateBody,
  ) =>
    request<DialpadReconciliationCheckAndRectifyOut>(
      `/dialpad/reconciliation/runs/check_and_rectify`,
      { method: "POST", body: JSON.stringify(body) },
    ),
  // ---------- Wave 10 (port-plan 24) — Generate Sales Scenarios ----------
  // All endpoints are admin/supadmin-gated server-side and scoped to the
  // team's community; the URLs stay under /teams/{id}/scenario-* (see
  // routes/scenarios.py).
  // Per-member viable + converted call counts for the inclusive 7-day
  // window ending on end_date (NZ calendar days).
  scenarioPool: (teamId: string, endDate: string, lookbackDays = 6) =>
    request<ScenarioPoolOut>(
      `/teams/${encodeURIComponent(teamId)}/scenario-pool?end_date=${encodeURIComponent(endDate)}&lookback_days=${lookbackDays}`,
      { method: "GET" },
    ),
  // Enqueue the outcome-split analysis (a multi-minute LLM run — always
  // async, never a long synchronous request). Cheap pre-flight failures
  // (missing resistance assessment, empty pool) come back as a readable
  // 422; otherwise 202 with the job id to poll via scenarioPreviewStatus.
  // Task #554 — Single select: `selectedAgentIds` narrows the pool to
  // just those team members; omit it for whole-team pooling.
  scenarioPreview: (teamId: string, endDate: string, selectedAgentIds?: string[], lookbackDays = 6) =>
    request<ScenarioPreviewEnqueuedOut>(
      `/teams/${encodeURIComponent(teamId)}/scenario-preview`,
      {
        method: "POST",
        body: JSON.stringify({
          end_date: endDate,
          ...(lookbackDays !== 6 ? { lookback_days: lookbackDays } : {}),
          ...(selectedAgentIds !== undefined
            ? { selected_agent_ids: selectedAgentIds }
            : {}),
        }),
      },
    ),
  scenarioPreviewStatus: (teamId: string, jobId: string) =>
    request<ScenarioPreviewStatusOut>(
      `/teams/${encodeURIComponent(teamId)}/scenario-preview/${encodeURIComponent(jobId)}`,
      { method: "GET" },
    ),
  // Latest persisted identification run + every tile's generation state.
  // Rehydrates Step 2 after leaving and returning; also the poll target
  // while any tile is building.
  scenarioRun: (teamId: string) =>
    request<ScenarioRunOut>(
      `/teams/${encodeURIComponent(teamId)}/scenario-run`,
      { method: "GET" },
    ),
  // Trigger one tile's scenario-JSON generation on the latest run (202;
  // tiles run concurrently and independently). Progress is read via
  // scenarioRun.
  scenarioGenerate: (teamId: string, scenarioIndex: number, expectedRunId?: string) =>
    request<ScenarioGenerateEnqueuedOut>(
      `/teams/${encodeURIComponent(teamId)}/scenario-generations`,
      {
        method: "POST",
        body: JSON.stringify({ scenario_index: scenarioIndex, expected_run_id: expectedRunId }),
      },
    ),
  // Download one Ready tile's validated scenario JSON. Returns the blob
  // plus the server-chosen filename (title-slug-<shortid>.json from
  // Content-Disposition) so the caller drives the anchor-click download.
  scenarioDownload: async (
    teamId: string,
    scenarioIndex: number,
    expectedRunId?: string,
  ): Promise<{ blob: Blob; filename: string }> => {
    const path = `/teams/${encodeURIComponent(teamId)}/scenario-generations/${scenarioIndex}/download`;
    const query = expectedRunId ? `?expected_run_id=${encodeURIComponent(expectedRunId)}` : "";
    return downloadScenarioFile(path + query, `scenario-${scenarioIndex + 1}.json`);
  },
  // Clear Scenarios: delete the team's identification runs (tile
  // generations cascade), resetting the Generate step to a clean slate.
  clearScenarioRuns: (teamId: string) =>
    request<void>(`/teams/${encodeURIComponent(teamId)}/scenario-runs`, {
      method: "DELETE",
    }),
};

async function downloadScenarioFile(path: string, fallback: string) {
    const res = await _fetch(path, { method: "GET" });
    if (!res.ok) await _throwApiError(res);
    let filename = fallback;
    const cd = res.headers.get("Content-Disposition");
    if (cd) {
      const m =
        /filename\*=UTF-8''([^;]+)/i.exec(cd) ||
        /filename="?([^";]+)"?/i.exec(cd);
      if (m && m[1]) {
        try {
          filename = decodeURIComponent(m[1]);
        } catch {
          filename = m[1];
        }
      }
    }
    return { blob: await res.blob(), filename };
}

export type BulkRequest = {
  request_id: string; end_date: string; lookback_days: number;
  include_team: boolean; team_count: number; include_individuals: boolean;
  individual_count: number; agent_ids: string[];
};
export type BulkStatus = {
  batch_id: string; end_date: string; lookback_days: number; status: string; error: string | null;
  expires_at?: string | null;
  targets: {
    id: string; kind: "team" | "individual"; name: string; agent_id: string | null;
    requested: number; available: number | null; generated: number;
    status: string; notes: string[]; error: string | null;
    outputs: { index: number; status: string; error: string | null }[];
  }[];
};
const batchPath = (team: string) => `/teams/${encodeURIComponent(team)}/scenario-batches`;
export const bulkApi = {
  create: (team: string, body: BulkRequest) =>
    request<{ batch_id: string }>(batchPath(team), { method: "POST", body: JSON.stringify(body) }),
  latest: (team: string, end: string, days: number) =>
    request<{ batch_id: string | null }>(`${batchPath(team)}?end_date=${encodeURIComponent(end)}&lookback_days=${days}`, { method: "GET" }),
  status: (team: string, id: string) =>
    request<BulkStatus>(`${batchPath(team)}/${encodeURIComponent(id)}`, { method: "GET" }),
  retry: (team: string, id: string) =>
    request(`${batchPath(team)}/${encodeURIComponent(id)}/retry`, { method: "POST" }),
  stop: (team: string, id: string) =>
    request(`${batchPath(team)}/${encodeURIComponent(id)}/stop`, { method: "POST" }),
  clear: (team: string, id: string) =>
    request(`${batchPath(team)}/${encodeURIComponent(id)}`, { method: "DELETE" }),
  download: (team: string, id: string, target?: string, index?: number) => {
    const query = target !== undefined
      ? `?target_id=${encodeURIComponent(target)}&scenario_index=${index}`
      : "";
    return downloadScenarioFile(
      `${batchPath(team)}/${encodeURIComponent(id)}/download${query}`,
      target === undefined ? "scenarios.zip" : `scenario-${(index ?? 0) + 1}.json`,
    );
  },
};

export type DialpadReconciliationSeverity =
  | "DEFINITELY_MISSING"
  | "SUSPECT_MISSING"
  | "SUSPECT_LOW"
  | "CLEAN"
  | "UNKNOWN";

// Task #385 — Reconciliation types
export type DialpadReconciliationRangePreset = "30d" | "60d" | "custom" | "all";
export type DialpadReconciliationStage =
  | "reported"
  | "checking"
  | "checked"
  | "correcting"
  | "corrected"
  | "cancelled"
  | "failed";

export type DialpadReconciliationRunCreateBody = {
  range_preset: DialpadReconciliationRangePreset;
  start?: string | null;
  end?: string | null;
  severities?: DialpadReconciliationSeverity[] | null;
};

export type DialpadReconciliationRunSummary = {
  id: string;
  range_preset: string;
  range_start: string;
  range_end: string;
  triggered_by: string | null;
  stage: DialpadReconciliationStage;
  auto_correct: boolean;
  suspect_days: number;
  clean_days: number;
  unknown_days: number;
  definitely_missing_days: number;
  scanned_days: number;
  confirmed_missing_calls: number;
  reingested_calls: number;
  failed_reingest_calls: number;
  check_job_id: string | null;
  correct_job_id: string | null;
  last_error: string | null;
  cancel_requested: boolean;
  progress_day: string | null;
  progress_day_calls: number;
  progress_suspect_total: number;
  progress_suspect_done: number;
  cancelled_at: string | null;
  created_at: string;
  updated_at: string;
};

export type DialpadReconciliationRunList = {
  runs: DialpadReconciliationRunSummary[];
  total: number;
  limit: number;
  offset: number;
};

export type DialpadReconciliationDayHourDetail = {
  hour: number;
  group_id?: string | null;
  direction?: string | null;
  observed: number;
  median?: number | null;
  p10?: number | null;
  sample_count?: number;
  flagged?: boolean;
  severity?: string | null;
};

export type DialpadReconciliationDay = {
  day: string;
  severity: DialpadReconciliationSeverity;
  observed_count: number;
  expected_median: number | null;
  detail: DialpadReconciliationDayHourDetail[];
  check_outcome:
    | "PENDING"
    | "CONFIRMED_MISSING"
    | "FALSE_ALARM"
    | "API_ANOMALY"
    | "SKIPPED";
  dialpad_count: number | null;
  missing_call_count: number;
  check_error: string | null;
  checked_at: string | null;
};

export type DialpadReconciliationMissingCall = {
  call_id: string;
  day: string;
  started_at: string | null;
  agent_id: string | null;
  agent_name: string | null;
  group_id: string | null;
  group_name: string | null;
  direction: string | null;
  duration_ms: number | null;
  status: "pending" | "queued" | "ingested" | "failed" | "skipped";
  ingest_job_id: string | null;
  ingest_window_id: string | null;
  last_error: string | null;
  updated_at: string;
};

export type DialpadReconciliationRunDetail = {
  run: DialpadReconciliationRunSummary;
  days: DialpadReconciliationDay[];
  missing_calls: DialpadReconciliationMissingCall[];
  missing_calls_total: number;
  missing_calls_truncated: boolean;
};

export type DialpadReconciliationActionOut = {
  run_id: string;
  job_id: string | null;
  stage: DialpadReconciliationStage;
};

export type DialpadReconciliationCheckAndRectifyOut = {
  run_id: string;
  check_job_id: string | null;
  stage: DialpadReconciliationStage;
};

// ---------- Assessments Ticket 13 — per-agent result row ----------

// Task #217 — per-measure outcome block carried on each result row so
// the Agent Profile results table can render pass/fail chips, compute
// a per-call score, and be filtered by a focused measure without a
// second round-trip. ``label_outcomes`` maps each label_name defined
// on the measure to the grader's boolean detection for this call.
export type AgentAssessmentResultMeasure = {
  measure_id: string;
  measure_label: string;
  label_outcomes: Record<string, boolean>;
};

export type AgentAssessmentResultRow = {
  id: string;
  call_id: string;
  assessment_id: string;
  assessment_name: string;
  assessment_version: number;
  status: "success" | "failed";
  summary: string | null;
  // Task #217 — empty list on failed rows.
  measures: AgentAssessmentResultMeasure[];
  error: string | null;
  model: string | null;
  input_tokens: number;
  output_tokens: number;
  latency_ms: number;
  run_id: string | null;
  started_at: string;
  // Task #217 — sentiment denormalised from dialpad_calls so the
  // results table can show a sentiment indicator per call.
  positive_sentiment: boolean;
  negative_sentiment: boolean;
  created_at: string;
};

export type AssessmentRunAllowedModels = {
  models: string[];
  current: string;
};

export type AssessmentRunAssignableTeam = {
  id: string;
  name: string;
  community_name: string;
  agent_count: number;
  assessment_count: number;
};

export type AssessmentRunScope =
  | { kind: "by_team"; team_id: string }
  | { kind: "all_eligible"; team_id?: null }
  | { kind: "retry_failed"; source_run_id: string };

export type AssessmentRunScanIn = {
  team_id: string | null;
  range_start: string;
  range_end: string;
  rerun_all?: boolean;
  model?: string | null;
  // Ticket 15 — per-run override of the assessment-run concurrency cap.
  // ``null``/``undefined`` falls through to the persisted setting then
  // the server-side env default. The backend clamps to the global
  // ceiling so out-of-range values can't starve a worker.
  concurrency?: number | null;
};

export type AssessmentRunSettings = {
  // Currently effective per-job concurrency cap (persisted value if
  // is_persisted is true, otherwise the env default).
  concurrency: number;
  // True iff a value is stored in app_settings; false means the env
  // default is in effect.
  is_persisted: boolean;
  // Env default (ASSESSMENT_RUN_PER_JOB_CONCURRENCY) — shown in the
  // "(env default)" trailing label when nothing is persisted.
  default_concurrency: number;
  // Global ceiling (ASSESSMENT_RUN_GLOBAL_CONCURRENCY) — used as the
  // input's max attribute and as the server-side clamp ceiling.
  max_concurrency: number;
};

export type AssessmentRunScanOut = {
  eligible_calls: number;
  assessments: number;
  candidate_pairs: number;
  skipped_pairs: number;
  inherited_assessments: number;
  estimated_input_tokens: number;
  estimated_output_tokens: number;
  model: string;
};

export type AssessmentRunProgress = {
  total: number;
  completed: number;
  failed: number;
  skipped: number;
};

export type AssessmentRunStatusValue =
  | "queued"
  | "running"
  | "completed"
  | "partial"
  | "failed"
  | "cancelled";

// Distinct terminal reason — surfaced alongside ``status`` so the UI can
// tell operator-initiated cancellation apart from a genuine handler
// failure or a scheduler lease reclaim. ``null`` while the run is still
// active. See ``_row_to_run_status`` in the API for the mapping rules.
export type AssessmentRunTerminalReason =
  | "cancelled"
  | "lease_lost"
  | "handler_error"
  | null;

export type AssessmentRunPerAssessment = {
  assessment_id: string;
  assessment_title: string;
  succeeded: number;
  failed: number;
  skipped: number;
  attempted: number;
  input_tokens: number;
  output_tokens: number;
};

export type AssessmentRunPerModel = {
  model: string | null;
  succeeded: number;
  failed: number;
  input_tokens: number;
  output_tokens: number;
};

export type AssessmentRunFailureCategory =
  | "missing_measure"
  | "invalid_json"
  | "content_filter"
  | "llm_api_error"
  | "other";

export type AssessmentRunFailureCategoryCount = {
  category: AssessmentRunFailureCategory;
  count: number;
};

export type AssessmentRunSummary = {
  succeeded: number;
  failed: number;
  skipped: number;
  attempted: number;
  input_tokens: number;
  output_tokens: number;
  avg_input_tokens: number | null;
  avg_output_tokens: number | null;
  per_assessment: AssessmentRunPerAssessment[];
  per_model: AssessmentRunPerModel[];
  failure_categories: AssessmentRunFailureCategoryCount[];
  model: string | null;
  duration_ms: number | null;
};

export type AssessmentRunFailedPair = {
  call_id: string;
  assessment_id: string;
  assessment_title: string;
  error: string | null;
  error_category: AssessmentRunFailureCategory;
  model: string | null;
  created_at: string | null;
};

export type AssessmentRunFailedPairsPage = {
  total: number;
  limit: number;
  offset: number;
  items: AssessmentRunFailedPair[];
};

// ---------- Assessments Ticket 11 — Aggregate response shapes ----------

export type AggregateRow = {
  measure_id: string;
  measure_label: string;
  label_name: string;
  detection_rate: number; // 0..1
  calls: number;
};

export type TeamAggregate = {
  team_id: string;
  team_name: string;
  range: { from: string; to: string };
  call_count: number;
  per_label: AggregateRow[];
  // Task #226 — optional prior-period overlay; empty / 0 when the
  // caller did not pass a paired prior window.
  prior_call_count?: number;
  prior_per_label?: AggregateRow[];
};

export type AgentAggregate = {
  agent_id: string;
  agent_name: string | null;
  range: { from: string; to: string };
  call_count: number;
  per_label: AggregateRow[];
  // Task #220 — optional prior-period overlay; empty / 0 when the
  // caller did not pass a paired prior window.
  prior_call_count?: number;
  prior_per_label?: AggregateRow[];
  // Task #221 — team-average per-(measure, label_name) detection rate
  // across the agent's team(s) for the same window. Empty when the
  // agent has no team membership so the FE falls back to the
  // agent-relative ordering.
  team_per_label?: AggregateRow[];
  // Task #264 — diagnostic counters powering the cause-aware empty
  // state on the Agent Profile "Assessment performance" panel.
  // ``total_calls_in_period`` counts visible viable dialpad_calls for
  // this agent inside the active window (whether or not scored).
  // ``scored_calls_all_time`` counts the agent's call_assessment_results
  // across all time (visible to the actor). ``team_has_assignment`` is
  // true when at least one team the agent belongs to has any row in
  // ``assessment_team_assignments``. All three are returned on every
  // response so the FE branches identically whether the section is
  // empty or populated; defaults keep older clients safe.
  total_calls_in_period?: number;
  scored_calls_all_time?: number;
  team_has_assignment?: boolean;
};

// ---------- Assessments Ticket 17/18 — by-assessment aggregates ----------
//
// Ticket 17 widens visibility on /assessments/aggregates/by-assessment to
// supadmin/admin/manager, returning one row per assessment in the
// requester's scope. Ticket 18 consumes the shape here for the
// "By assessment" mode of Teams view.

export type AssessmentAggregateTeamRow = {
  team_id: string;
  team_name: string;
  call_count: number;
  per_label: AggregateRow[];
  // Task #211 — optional prior-period overlay (empty / 0 when the
  // caller did not pass a paired prior window).
  prior_call_count?: number;
  prior_per_label?: AggregateRow[];
};

export type AssessmentAggregateAgentRow = {
  agent_id: string;
  agent_name: string | null;
  team_id: string;
  team_name: string;
  call_count: number;
};

// Task #167 — per-day graded call counts for one assessment. Task
// #196 added ``detection_rate`` so the Teams view's
// per-team / per-assessment cards can plot a quality trend instead
// of a redundant volume trend. ``null`` means "no graded calls that
// day" so the line chart can render a gap rather than a misleading
// 0% spike.
export type AssessmentTrendPoint = {
  date: string; // YYYY-MM-DD (UTC)
  calls: number;
  detection_rate: number | null;
};

// Task #197 — triage payload shapes for the redesigned "Team
// performance — Assessments" card. ``prior_*`` is empty when the
// caller omits the ``prior_from``/``prior_to`` window (e.g. on
// All-time, where there's no defined prior to compare against).
export type TeamTriageRow = {
  team_id: string;
  team_name: string;
  call_count: number;
  per_label: AggregateRow[];
  prior_call_count: number;
  prior_per_label: AggregateRow[];
};

export type AgentMeasureRow = {
  agent_id: string;
  agent_name: string | null;
  call_count: number;
  detection_rate: number; // 0..1 — focused measure or overall
};

export type AssessmentAggregate = {
  assessment_id: string;
  assessment_name: string;
  assessment_version: number;
  range_from: string;
  range_to: string;
  call_count: number;
  per_label: AggregateRow[];
  teams: AssessmentAggregateTeamRow[];
  agents: AssessmentAggregateAgentRow[];
};

// Re-export Ticket 18's view-mode alias so callers can keep their imports
// rooted in lib/api without a second router dep.
export type { ViewMode } from "./router";

export type AssessmentRunStatus = {
  run_id: string;
  status: AssessmentRunStatusValue;
  progress: AssessmentRunProgress;
  summary: AssessmentRunSummary;
  scope: AssessmentRunScope | null;
  range_start: string | null;
  range_end: string | null;
  sync_window_id: string | null;
  source_run_id: string | null;
  model: string | null;
  started_at: string | null;
  completed_at: string | null;
  error: string | null;
  terminal_reason?: AssessmentRunTerminalReason;
};

export type DeleteResultsBody = {
  from: string;
  to: string;
  team_id?: string | null;
  assessment_id?: string | null;
};

export type DeleteResultsGroupCount = {
  id: string;
  name: string;
  count: number;
};

export type DeleteResultsPreview = {
  matched_count: number;
  by_assessment: DeleteResultsGroupCount[];
  by_team: DeleteResultsGroupCount[];
  earliest: string | null;
  latest: string | null;
};

export type DeleteResultsOut = {
  deleted_count: number;
  deleted_jobs: number;
  from: string;
  to: string;
  team_id: string | null;
  assessment_id: string | null;
};

export type AnalyticsTeamAgentSentimentRow = {
  agent_id: string;
  full_name: string | null;
  viable: number;
  positive: number;
  negative: number;
};

export type AssessmentRow = {
  id: string;
  name: string;
  status: "draft" | "saved";
  teams: { id: string; name: string }[];
};

// Initial epic ships only multi_label. Type is a discriminated union for
// forward-compatibility — future kinds add new variants.
export type MeasureInput =
  | {
      kind: "multi_label";
      config: {
        label: string;
        labels: { name: string; detection_phrases: string[] }[];
      };
    };

export type AssessmentInput = {
  name: string;
  description?: string;
  ai_instructions?: string;
  measures: MeasureInput[];
};

export type MeasureOut = {
  id: string;
  kind: string;
  config: Record<string, unknown>;
  order_index: number;
};

export type AssessmentTeamRef = { id: string; name: string };

export type AssignableAssessment = {
  id: string;
  name: string;
  description: string | null;
};

export type AssessmentDetail = {
  id: string;
  name: string;
  description: string | null;
  ai_instructions: string | null;
  status: "draft" | "saved";
  version: number;
  measures: MeasureOut[];
  teams: AssessmentTeamRef[];
};

// Slim read-only projection returned by the scoped admin/manager
// endpoint. No AI instructions, status/version, teams or history.
export type AssessmentReadOnlyDetail = {
  id: string;
  name: string;
  description: string | null;
  measures: MeasureOut[];
};

export type AssessmentChangeKind =
  | "created"
  | "name"
  | "description"
  | "ai_instructions"
  | "measures"
  | "status"
  | "team_assignments"
  | "deleted";

export type AssessmentChangeLogEntry = {
  id: string;
  change_kind: AssessmentChangeKind | string;
  changed_at: string;
  changed_by_id: string | null;
  changed_by_name: string | null;
  changed_by_email: string | null;
  before: unknown | null;
  after: unknown | null;
};

export type AssessmentChangeLogPage = {
  entries: AssessmentChangeLogEntry[];
  next_offset: number | null;
  total: number;
};

export type AssessmentPatch = {
  name?: string;
  description?: string | null;
  ai_instructions?: string | null;
  measures?: MeasureInput[];
};

export type AnalyticsTeamAgent = {
  agent_id: string;
  agent_name: string | null;
};

export type TimeRange =
  | "daily"
  | "weekly"
  | "monthly"
  | "quarterly"
  | "six_month"
  | "all_time"
  // Task #365 — "Custom" lets the user pick an inclusive [start, end]
  // day window. When this value is sent, the ``start`` and ``end``
  // query params are required (YYYY-MM-DD, UTC, inclusive).
  | "custom";
export type RangeMode = "calendar" | "rolling";
// Task #365 — paired YYYY-MM-DD strings carried alongside ``timeRange``
// for the "custom" preset. ``null`` everywhere else.
export type CustomDateRange = { start: string; end: string };
/** @deprecated Use RangeMode — kept for callers mid-migration. */
export type CompareMode = RangeMode;

export type AnalyticsTeamOption = {
  id: string;
  name: string;
  community_id: string;
  community_name: string;
};
export type AnalyticsScope = {
  role: Role;
  selected_team_id: string | null;
  selected_agent_id: string | null;
  team_options: AnalyticsTeamOption[];
};
export type AnalyticsMetrics = {
  registered: number;
  viable: number;
  positive: number;
  negative: number;
  quality: number;
};
export type AnalyticsFunnelStep = { key: string; label: string; count: number };
export type AnalyticsDailyPoint = {
  date: string;
  registered: number;
  viable: number;
  quality: number;
  positive: number;
  negative: number;
};
export type AnalyticsHourlyPoint = {
  hour: number;
  registered: number;
  viable: number;
  quality: number;
  positive: number;
  negative: number;
  registered_total: number;
  viable_total: number;
  quality_total: number;
  positive_total: number;
  negative_total: number;
};
export type AnalyticsWeekdayPoint = {
  weekday: number; // Mon=0 … Sun=6
  registered: number;
  viable: number;
  quality: number;
  positive: number;
  negative: number;
  registered_total: number;
  viable_total: number;
  quality_total: number;
  positive_total: number;
  negative_total: number;
};
export type AnalyticsTeamBreakdown = {
  team_id: string;
  team_name: string;
  registered: number;
  viable: number;
  quality: number;
  positive: number;
  negative: number;
};
export type AnalyticsAgentFlags = {
  speaking_too_fast: boolean;
  monologuing: boolean;
  zero_negative: boolean;
};
export type AnalyticsAgentRow = {
  agent_id: string;
  full_name: string | null;
  team_name: string;
  registered: number;
  viable: number;
  positive: number;
  negative: number;
  quality: number;
  avg_duration_ms: number | null;
  flags: AnalyticsAgentFlags;
};
export type AnalyticsMetricDelta = {
  absolute: number;
  pct: number | null;
};
export type AnalyticsDeltas = {
  registered: AnalyticsMetricDelta;
  viable: AnalyticsMetricDelta;
  positive: AnalyticsMetricDelta;
  negative: AnalyticsMetricDelta;
  quality: AnalyticsMetricDelta;
};
export type AnalyticsPeriod = {
  start: string | null;
  end: string | null;
};
export type AnalyticsDashboard = {
  scope: AnalyticsScope;
  time_range: TimeRange;
  compare: boolean;
  period: AnalyticsPeriod;
  prior_period: AnalyticsPeriod | null;
  metrics: AnalyticsMetrics;
  prior_metrics: AnalyticsMetrics | null;
  deltas: AnalyticsDeltas | null;
  funnel: AnalyticsFunnelStep[];
  daily: AnalyticsDailyPoint[];
  prior_daily: AnalyticsDailyPoint[] | null;
  hourly: AnalyticsHourlyPoint[];
  prior_hourly: AnalyticsHourlyPoint[] | null;
  weekday: AnalyticsWeekdayPoint[];
  prior_weekday: AnalyticsWeekdayPoint[] | null;
  team_viable: AnalyticsTeamBreakdown[];
  team_sentiment: AnalyticsTeamBreakdown[];
  agents: AnalyticsAgentRow[];
};

export type UnassignedCall = {
  call_id: string;
  started_at: string | null;
  duration_ms: number | null;
  direction: string | null;
  agent_id: string | null;
  agent_name: string | null;
};

// -- Access-control types --
export type CommunityStatus = "active" | "draft";
export type ExceptionMode = "include" | "exclude";
export type CommunityAgentException = {
  dialpad_user_id: string;
  mode: ExceptionMode;
};
export type CommunityOut = {
  id: string;
  name: string;
  status: CommunityStatus;
  brands: string[];
  exceptions: CommunityAgentException[];
  admin_user_ids: string[];
  agent_count: number;
  is_default: boolean;
  created_at: string;
  updated_at: string;
};
export type CommunityIn = {
  name: string;
  status: CommunityStatus;
  brands: string[];
  exceptions: CommunityAgentException[];
  admin_user_ids: string[];
  is_default: boolean;
};
export type TeamOut = {
  id: string;
  community_id: string;
  community_name: string;
  name: string;
  manager_user_ids: string[];
  dialpad_user_ids: string[];
  call_count: number;
  agent_count: number;
  assessments: { id: string; name: string }[];
  assessment_count: number;
  // Plan 31 — whether Generate Sales Scenarios is available for this team.
  scenarios_enabled: boolean;
  created_at: string;
  updated_at: string;
};
export type TeamIn = {
  community_id: string;
  name: string;
  manager_user_ids: string[];
  dialpad_user_ids: string[];
};
export type MyTeam = {
  id: string;
  name: string;
  community_id: string;
  community_name: string;
};
export type AdminScopedCommunity = {
  id: string;
  name: string;
  status: CommunityStatus;
  brands: string[];
  exceptions: CommunityAgentException[];
};
export type AgentProfileHeader = {
  agent_id: string | null;
  full_name: string | null;
  email: string | null;
  brand: string;
  license: string | null;
  coaching_team: string | null;
};
export type AgentProfileSummary = {
  assessed_calls: number;
  avg_duration_ms: number | null;
  quality_call_count: number;
  quality_call_pct: number;
  avg_moments_per_call: number;
};
export type AgentQualitySignals = {
  positive_pct: number;
  action_items_pct: number;
  call_purpose_pct: number;
  negative_pct: number;
};
export type AgentCoachingFlags = {
  speaking_too_quickly_pct: number;
  monologuing_pct: number;
  swearing_count: number;
};
export type AgentDurationBuckets = {
  under_1_min: number;
  one_to_three_min: number;
  three_to_five_min: number;
  five_plus_min: number;
};
export type TopMoment = { key: string; label: string; count: number };
export type AgentGroupRow = {
  group_id: string;
  group_type: string | null;
  name: string | null;
};
export type AgentTeamRow = {
  id: string;
  name: string;
  community_id: string;
  community_name: string;
};
export type AgentTeamAverage = {
  calls: number | null;
  avg_duration_ms: number | null;
  positive_pct: number | null;
  negative_pct: number | null;
};
export type AgentLabel = {
  key: string;
  label: string;
  count: number;
  custom: boolean;
};
export type AgentProfile = {
  header: AgentProfileHeader;
  summary: AgentProfileSummary;
  quality: AgentQualitySignals;
  coaching: AgentCoachingFlags;
  duration_buckets: AgentDurationBuckets;
  top_moments: TopMoment[];
  groups: AgentGroupRow[];
  teams: AgentTeamRow[];
  team_average: AgentTeamAverage;
  labels: AgentLabel[];
};

export type AgentRowGroup = {
  group_id: string;
  group_type: string | null;
  name: string | null;
};

export type AgentCallSlice =
  | "fast"
  | "mono"
  | "negative"
  | "positive"
  | "quality";

export type AgentCallRow = {
  call_id: string;
  started_at: string | null;
  duration_ms: number | null;
  direction: string | null;
  contact_name: string | null;
  contact_phone: string | null;
  recap_summary: string | null;
  positive_sentiment: boolean;
  negative_sentiment: boolean;
  speaking_too_quickly: boolean;
  monologuing: boolean;
  quality_call: boolean;
};

export type AgentCallsOut = {
  rows: AgentCallRow[];
  total: number;
  slice: AgentCallSlice | null;
};

export type CallDetailFlag = "fast" | "mono" | "quality";

export type CallDetailSegment = {
  speaker: string | null;
  user_id: string | null;
  text: string | null;
  time: string | null;
  flags: CallDetailFlag[];
  // Task #155: index into the numbered-turns spine, or null when the
  // segment was filtered out of the turn indexer.
  turn: number | null;
};

// Task #155 — Call Detail assessments block.
export type CallAssessmentState =
  | "success_with_evidence"
  | "no_evidence"
  | "failed"
  | "not_scored";

export type CallAssessmentLabel = {
  name: string;
  detected: boolean;
  turns: number[];
};

export type CallAssessmentMeasure = {
  measure_id: string;
  measure_label: string;
  order_index: number;
  detected_count: number;
  total_labels: number;
  labels: CallAssessmentLabel[];
};

export type CallAssessmentEvidenceStatus =
  | "ok"
  | "missing"
  | "failed"
  | "no_spans"
  | "disabled";

export type CallAssessmentForCall = {
  assessment_id: string;
  assessment_name: string;
  state: CallAssessmentState;
  summary: string | null;
  error: string | null;
  measures: CallAssessmentMeasure[];
  evidence_status: CallAssessmentEvidenceStatus;
};

export type CallAssessmentSummaryStatus =
  | "ready"
  | "generating"
  | "no_data"
  | "disabled"
  | "failed";

export type CallAssessmentSummary = {
  status: CallAssessmentSummaryStatus;
  text: string | null;
  job_id: string | null;
  model: string | null;
  updated_at: string | null;
  error: string | null;
};

export type CallDetail = {
  call_id: string;
  started_at: string | null;
  duration_ms: number | null;
  direction: string | null;
  contact_name: string | null;
  contact_phone: string | null;
  agent_id: string | null;
  agent_name: string | null;
  agent_email: string | null;
  recap_summary: string | null;
  recap_disposition: string | null;
  recap_purposes: string[];
  positive_sentiment: boolean;
  negative_sentiment: boolean;
  speaking_too_quickly: boolean;
  monologuing: boolean;
  quality_call: boolean;
  has_transcript: boolean;
  transcript_text: string | null;
  segments: CallDetailSegment[];
  assessments_for_call: CallAssessmentForCall[];
  call_summary: CallAssessmentSummary;
};

// Task #331 — Search page wire types.
export type ClientPhoneSuggestionsOut = { rows: string[] };
// Task #350 — agent-name search on the (renamed) Search page.
export type ClientAgentSuggestionRow = {
  agent_id: string;
  display_name: string;
};
export type ClientAgentSuggestionsOut = {
  rows: ClientAgentSuggestionRow[];
};
export type ClientCallRow = {
  call_id: string;
  started_at: string | null;
  direction: string | null;
  duration_ms: number | null;
  agent_id: string | null;
  agent_name: string | null;
  // Kept in the payload for the Team *filter* even though the results
  // table no longer renders a Team column (T9).
  team_name: string | null;
  // T9 (#397) — customer phone per row so the Search results table can
  // identify who each call was with in both Number and Agent modes.
  contact_phone: string | null;
  has_transcript: boolean;
  assessed: boolean;
};
export type ClientCallsOut = { rows: ClientCallRow[] };

// Task #336 — "Ask a Question" Q&A chat on the Search page.
// Task #350 — exactly one of ``phone`` / ``agent_id`` must be set
// (phone-mode vs agent-mode); the server rejects neither/both.
export type ClientQaIn = {
  phone?: string | null;
  agent_id?: string | null;
  question: string;
  call_ids: string[];
};
// PET-645 — per-call phone + occurrence-index citation computed over
// the answer's analysed call pool. Raw call IDs in the answer text get
// swapped for "<phone> (<ordinal>)" links; phone-less calls carry
// null/null and fall back to a plain linked call ID.
export type CallRef = {
  call_id: string;
  contact_phone: string | null;
  phone_ordinal: number | null;
  // Set on alias entries (a Dialpad duplicate-leg ID for the same
  // conversation): the viable call the alias resolves to. Null on
  // canonical entries.
  canonical_call_id?: string | null;
};
export type ClientQaOut = {
  answer: string;
  model: string;
  calls_used: number;
  call_refs: CallRef[];
};

// Wave 5 · T1 — Roundtable multi-agent comparison Q&A. Wire shapes
// mirror the API models (apps/api models/dialpad.py: RoundtableQaIn /
// RoundtableAgentAnswer / RoundtableQaOut). ``time_range`` /
// ``range_mode`` / ``start`` / ``end`` reuse the dashboard date-range
// vocabulary; a bounded range BOUNDS each agent's call pool server-side
// (it never tops up from outside the window).
export type RoundtableDirection = "both" | "inbound" | "outbound";
export type RoundtableQaIn = {
  agent_ids: string[];
  question: string;
  time_range: TimeRange;
  range_mode: RangeMode;
  start?: string | null;
  end?: string | null;
  direction: RoundtableDirection;
  has_transcript: boolean;
};
// Structured per-agent failure category (#205 semantics — never error
// prose smuggled into the answer string). ``content_filter`` is a
// provider safety block: deterministic for a given input, never retried.
export type RoundtableErrorKind =
  | "content_filter"
  | "invalid"
  | "upstream"
  | "timeout";
export type RoundtableAgentAnswer = {
  agent_id: string;
  agent_name: string | null;
  // Grounded answer text (or the canned "no calls match" message), or
  // null when this agent's answer failed — then ``error`` carries the
  // server copy and ``error_kind`` the category.
  answer: string | null;
  error: string | null;
  error_kind: RoundtableErrorKind | null;
  // Server-truth counts: every call matching the filters vs the ≤100
  // most recent actually analysed, so the UI can say "answered from
  // 100 of 412 calls" honestly.
  call_count: number;
  calls_used: number;
  // PET-659 — the date range the analysed calls actually span: earliest /
  // latest ``started_at`` (ISO) among the ≤100 calls that fed this
  // agent's answer. Null when the agent had no analysable calls. Differs
  // per agent because call volumes differ.
  range_start?: string | null;
  range_end?: string | null;
};
export type RoundtableQaOut = {
  question: string;
  answers: RoundtableAgentAnswer[];
  comparison: string;
  // Phone + occurrence-index citations computed over the union of every
  // agent's analysed pool for THIS question (incl. duplicate-leg alias
  // entries) — ordinals are scoped per question block, never thread-wide.
  call_refs: CallRef[];
  model: string | null;
};

export type AgentRow = {
  agent_id: string | null;
  full_name: string | null;
  email: string | null;
  brand: string;
  calls: number;
  // Task #377 — total call volume (viable or not) for this agent in
  // the active period range. Powers the new Total Calls metric on
  // the Compare Agents cards. Always >= ``calls``.
  total_calls: number;
  avg_duration_ms: number | null;
  quality_call_count: number;
  positive_count: number;
  negative_count: number;
  too_fast_count: number;
  action_items_count: number;
  // Task #377 — distinct viable converting calls in the period (from
  // the Client Conversion assessment). ``null`` when the agent's
  // in-scope team(s) aren't enrolled in any Client Conversion
  // assessment, so the UI can render "—".
  converting_count: number | null;
  groups: AgentRowGroup[];
};

export type DialpadSettings = {
  min_transcript_duration_ms: number;
  allowed_values: number[];
  sync_buttons_disabled: boolean;
};

// ADR 0036 — call-data retention. Two numbers on purpose: the dashboard shows
// `display_days` (365) while deletion happens at `purge_days` (395). The panel
// spells the gap out rather than rounding both to "a year".
export type RetentionSettings = {
  /** Whether this environment is permitted to delete at all. Deploy-gated. */
  enabled: boolean;
  /** The runtime brake — the only field this panel can change. */
  paused: boolean;
  display_days: number;
  purge_days: number;
  grace_days: number;
  cutoff_date: string;
  /**
   * Served by the API on purpose. A worker cannot report its own death, so
   * the container that outlives it is the one that has to be able to say
   * "cleanup has not actually run in N hours". Both flags are true only when
   * the purge is enabled *and* not paused — a deliberately-off purge is not
   * a fault, and must not be shown as one.
   */
  overdue: boolean;
  never_run: boolean;
  hours_since_last_run: number | null;
  run_at_local: string;
  last_run: {
    at: string;
    cutoff: string;
    calls_deleted: number;
    /** False when the batch ceiling stopped the run with rows still eligible. */
    exhausted: boolean;
  } | null;
};

// PET-641 / PET-672 — admin-curated suggested questions for the chat
// surfaces. ``teams`` is either the literal "all" (everyone) or a list of
// team-id strings (empty array == unassigned / "no teams"). ``color`` is a
// palette token (see questionColors.ts) or null (no colour).
export type SuggestedQuestion = {
  id: string;
  text: string;
  color: string | null;
  teams: "all" | string[];
};
export type SuggestedQuestionsOut = {
  questions: SuggestedQuestion[];
  max_questions: number;
  colors: string[];
};

export type DialpadRange = { start: string; end: string };
export type DialpadPreview = {
  requested: DialpadRange;
  already_covered: DialpadRange[];
  gaps: DialpadRange[];
  fully_covered: boolean;
};
export type DialpadIngestResult = {
  queued: { sync_window_id: string; job_id: string | null; start: string; end: string }[];
  fully_covered: boolean;
  batch_id?: string | null;
};
export type DialpadSyncWindow = {
  id: string;
  window_start: string;
  window_end: string;
  status:
    | "pending"
    | "running"
    | "complete"
    | "complete_with_errors"
    | "failed";
  phase?: string | null;
  phases?: string | null;
  calls_found: number;
  calls_ingested: number;
  transcripts_ingested: number;
  transcripts_skipped_short?: number;
  transcripts_skipped_no_duration?: number;
  min_duration_ms?: number | null;
  errors: number;
  recap_errors?: number;
  triggered_by: string | null;
  job_id: string | null;
  last_error: string | null;
  last_run_mode?: string | null;
  created_at: string;
  updated_at: string;
  duration_ms?: number | null;
  users_groups_status?: string | null;
  users_upserted?: number | null;
  groups_upserted?: number | null;
  users_groups_started_at?: string | null;
  users_groups_finished_at?: string | null;
  users_groups_error?: string | null;
  assessment_run?: DialpadLinkedAssessmentRun | null;
  batch_id?: string | null;
};
export type DialpadLinkedAssessmentRun = {
  run_id: string;
  status: "queued" | "running" | "completed" | "partial" | "failed";
  progress: {
    total: number;
    completed: number;
    failed: number;
    skipped: number;
  };
};
export type DialpadUserRow = {
  id: string;
  display_name: string | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  emails: string[] | null;
  brand: string | null;
  state: string | null;
  license: string | null;
  is_admin: boolean | null;
  is_super_admin: boolean | null;
  office_id: string | null;
  group_details: Array<{
    group_id?: string;
    group_type?: string;
    name?: string;
    [k: string]: unknown;
  }> | null;
  synced_at: string;
};
export type DialpadUsersResponse = {
  rows: DialpadUserRow[];
  total: number;
  by_brand: Record<string, number>;
  last_synced_at: string | null;
};
export type DialpadMissingRange = {
  start: string;
  end: string;
  days: number;
};
export type DialpadDateCoverage = {
  first_call_at: string | null;
  last_call_at: string | null;
  total_days: number;
  days_with_calls: number;
  missing_days: number;
  missing_ranges: DialpadMissingRange[];
  largest_gap: DialpadMissingRange | null;
  truncated: boolean;
  total_missing_ranges: number;
};
export type DialpadMissingTranscripts = {
  count: number;
  first_at: string | null;
  last_at: string | null;
};
export type DialpadSyncSummary = {
  covered_start: string | null;
  covered_end: string | null;
  days_covered: number;
  total_calls: number;
  viable_calls: number;
  // Count of dialpad_transcripts rows attached to calls inside the
  // union of completed sync windows.
  transcripts: number;
  avg_calls_per_day: number | null;
  avg_viable_per_day: number | null;
  viable_pct: number | null;
  avg_first_call_seconds: number | null;
  avg_last_call_seconds: number | null;
  // Task #303 — assessment coverage on synced calls visible to the
  // caller. ``assessed_calls`` is the number of distinct calls with at
  // least one assessment result (any assessment). ``assessed_ranges``
  // are the contiguous NZ-local-date runs those calls' started_at
  // covers, capped server-side with ``assessed_truncated`` /
  // ``assessed_total_ranges`` mirroring the date_coverage pattern.
  assessed_calls: number;
  assessed_ranges: DialpadMissingRange[];
  assessed_truncated: boolean;
  assessed_total_ranges: number;
};
export type DialpadFailedAssessments = {
  count: number;
  failed_ranges: DialpadMissingRange[];
  truncated: boolean;
  total_failed_ranges: number;
};
export type DialpadReadyForAssessment = {
  // T10 — downloaded calls with a transcript whose agent belongs to a
  // team that has at least one saved assessment, and that have no
  // assessment result yet. Not filtered on viability or sync window.
  count: number;
  ranges: DialpadMissingRange[];
  truncated: boolean;
  total_ranges: number;
};
export type DialpadCoverage = {
  date_coverage: DialpadDateCoverage;
  missing_transcripts: DialpadMissingTranscripts;
  ready_for_assessment: DialpadReadyForAssessment;
  sync_summary: DialpadSyncSummary;
  failed_assessments: DialpadFailedAssessments;
  has_calls: boolean;
};
export type DialpadRetryMissingTranscriptsActive = {
  has_active: boolean;
  job_id: string | null;
  status: string | null;
  candidates: number | null;
  fetched: number | null;
  missing: number | null;
  errors: number | null;
  pending: number | null;
};
export type TrainingTipsCallRef = {
  call_id: string;
  contact_phone: string | null;
  phone_ordinal: number | null;
  // Set on alias entries (a Dialpad duplicate-leg ID for the same
  // conversation): the viable call the alias resolves to. Null on
  // canonical entries.
  canonical_call_id?: string | null;
};

export type TrainingTipsOut = {
  // Task #349 — server-generated handle. Pass to
  // `generateTrainingModule` to derive a training module from this
  // exact report without round-tripping the markdown back to the API.
  report_id: string;
  report: string;
  model: string;
  calls_used: number;
  truncated: boolean;
  call_refs: TrainingTipsCallRef[];
};

export type DialpadActive = {
  has_active: boolean;
  batch_id?: string | null;
  total_windows?: number | null;
  completed_windows?: number | null;
  sync_window_id?: string | null;
  job_id?: string | null;
  job_status?: string | null;
  phase?: string | null;
  phases?: string | null;
  window_start?: string | null;
  window_end?: string | null;
  calls_found?: number | null;
  calls_ingested?: number | null;
  transcripts_ingested?: number | null;
  transcripts_skipped_short?: number | null;
  transcripts_skipped_no_duration?: number | null;
  min_duration_ms?: number | null;
  errors?: number | null;
  updated_at?: string | null;
  users_groups_status?: string | null;
  users_upserted?: number | null;
  groups_upserted?: number | null;
  users_groups_started_at?: string | null;
  users_groups_finished_at?: string | null;
  users_groups_error?: string | null;
  assessment_run?: DialpadLinkedAssessmentRun | null;
  // Per-phase status aggregated across the batch: "waiting" | "running" |
  // "partial" | "done". Drives the live card's phase boxes so a later
  // phase never renders ahead of an earlier one.
  calls_status?: string | null;
  transcripts_status?: string | null;
  assessments_status?: string | null;
  // Count of windows that have FINISHED each phase (out of total_windows), so
  // the live card can show "K/N windows" for a parallel multi-day batch.
  calls_windows_done?: number | null;
  transcripts_windows_done?: number | null;
  assessments_windows_done?: number | null;
};

export type SyncSchedule = {
  id: string;
  label: string | null;
  time_of_day: string;
  days_of_week: number[];
  is_system: boolean;
  is_enabled: boolean;
  window_strategy: string;
  lookback_minutes: number | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  last_fired_at: string | null;
  last_batch_id: string | null;
};

// ---------------------------------------------------------------------------
// Wave 10 (port-plan 24) — Generate Sales Scenarios. Mirrors
// apps/api/src/convoai_api/models/scenarios.py.
// ---------------------------------------------------------------------------

// One team member's slice of the transcript pool. Not-converted is
// derivable as viable_call_count - converted_call_count, so one response
// serves all three outcome columns.
export type ScenarioPoolMember = {
  dialpad_user_id: string;
  name: string;
  viable_call_count: number;
  converted_call_count: number;
};

export type ScenarioPoolOut = {
  outcome_label?: string;
  team_id: string;
  team_name: string;
  window_start: string;
  window_end: string;
  members: ScenarioPoolMember[];
};

// One resistance measure with its exact distinct-call count (a call
// counts once per measure) — computed server-side in SQL/Python, never
// by the LLM.
export type ScenarioMeasureCount = {
  name: string;
  call_count: number;
};

// One outcome bucket's resistance distribution: the bucket's
// distinct-call total plus its top measures, most common first.
export type ScenarioDistribution = {
  total_calls: number;
  top_measures: ScenarioMeasureCount[];
};

// One suggested-scenario tile. `resistance_measures` comes from the
// SQL-mined not-converted label combination that seeded the scenario
// (deterministic); the text fields are the LLM's summary (expanded into
// a full scenario file by the per-tile Generate step).
export type SuggestedScenario = {
  title: string;
  product: string;
  scenario_type: string;
  customer_personality: string;
  pet_background: string;
  resistance_measures: string[];
};

type CoverageGroup = { group: string; eligible: number; sampled: number };

export type ScenarioPreviewOut = {
  run_id?: string | null;
  expires_at?: string | null;
  lookback_days?: number;
  selected_agent_ids?: string[] | null;
  sampling?: {
    viable: number;
    eligible: number;
    sampled: number;
    resistance_detected: number;
    max_calls: number;
    sections: { start: string; end: string; eligible: number; sampled: number }[];
    agents: CoverageGroup[];
    outcomes: CoverageGroup[];
    resistance: CoverageGroup[];
    exclusions: Record<string, number>;
  } | null;
  outcome_label?: string;
  config_revision?: number | null;
  team_id: string;
  window_start: string;
  window_end: string;
  calls_analysed: number;
  converted_count: number;
  not_converted_count: number;
  model: string;
  converted: ScenarioDistribution;
  not_converted: ScenarioDistribution;
  // Short LLM narrative contrasting the buckets (prose only — all
  // displayed numbers come from the distributions above).
  narrative: string;
  // 3–5 tiles (fewer when the not-converted data is thin).
  suggested_scenarios: SuggestedScenario[];
  // Graceful-degradation notes for thin pools.
  notes: string[];
};

// 202 response for the enqueue POST — poll scenarioPreviewStatus with
// this job_id until a terminal status.
export type ScenarioPreviewEnqueuedOut = {
  job_id: string;
  status: string;
};

// Polled status for one scenario_preview job. `result` is populated only
// once the job has succeeded; `error` carries the job's readable error
// (e.g. "ScenarioAnalysisError: …") for the failed/dead states and is
// displayed verbatim.
export type ScenarioPreviewStatusOut = {
  status: JobStatus;
  error: string | null;
  result: ScenarioPreviewOut | null;
};

export type ScenarioGenerationStatus = "building" | "ready" | "error";

// One tile's generation state. `filename` is set only when ready (the
// download endpoint serves the same name).
export type ScenarioGeneration = {
  scenario_index: number;
  status: ScenarioGenerationStatus;
  error: string | null;
  filename: string | null;
};

// Rehydration payload: the latest non-expired identification run
// reconstructed into the same report shape the live preview poll
// returns, plus every tile's generation state. `run` is null when no
// run exists.
export type ScenarioRunOut = {
  generation_blocked_reason?: string | null;
  run: ScenarioPreviewOut | null;
  generations: ScenarioGeneration[];
};

// 202 response for the per-tile trigger — the client re-polls
// scenarioRun (which carries every tile's state).
export type ScenarioGenerateEnqueuedOut = {
  generation_id: string;
  scenario_index: number;
  status: string;
};

// Team-specific business policy; transport and export contracts remain code-owned.
export type ScenarioMeasure = { assessment_id: string; measure_id: string; label: string };
export type ScenarioCriteria = Record<"fail" | "partial" | "pass" | "bonus", string>;
export type ScenarioSettings = {
  version: 1;
  success_measure: ScenarioMeasure | null;
  instructions: string;
  criteria: Record<"beginner" | "intermediate" | "advanced", ScenarioCriteria> | null;
};
export type ScenarioSettingsUpdate = { revision: number; enabled: boolean; settings: ScenarioSettings };
export type ScenarioSettingsOut = ScenarioSettingsUpdate & {
  setup_required: string | null;
  options: (ScenarioMeasure & { assessment_name: string; measure_name: string })[];
};
